/**
 * Widget Tag Filter Editor (issue #489)
 *
 * Lets a user scope a dashboard widget's data to entities matching a tag or
 * TagCategory filter (include/exclude, AND/OR), reusing the existing
 * `TagPicker` for tag selection. See homepage-dashboard-spec.md
 * "Widget Tag Filtering & Custom Names".
 */

import { useTagCategories } from '../../graphql/queries';
import { TagPicker } from '../tags/TagPicker';
import {
  buildDashboardWidgetFilter,
  parseDashboardWidgetFilter,
  type WidgetTagFilterFormState,
} from '../../lib/widgetTagFilters';
import type { DashboardWidgetFilter, TagFilterCombiner } from '../../types/preferences';
import '@tylertech/forge/button';
import './WidgetTagFilterEditor.css';

export interface WidgetTagFilterEditorProps {
  filters: DashboardWidgetFilter | undefined;
  onChange: (filters: DashboardWidgetFilter | undefined) => void;
}

export function WidgetTagFilterEditor({
  filters,
  onChange,
}: WidgetTagFilterEditorProps): JSX.Element {
  const form = parseDashboardWidgetFilter(filters);
  const { data: categoriesData } = useTagCategories();
  const categories = (categoriesData?.tagCategories ?? []).filter((c) => c.status === 'active');

  const emit = (next: WidgetTagFilterFormState): void => {
    onChange(buildDashboardWidgetFilter(next));
  };

  const toggleCategory = (categoryId: string): void => {
    const selected = form.includeCategoryIds.includes(categoryId);
    emit({
      ...form,
      includeCategoryIds: selected
        ? form.includeCategoryIds.filter((id) => id !== categoryId)
        : [...form.includeCategoryIds, categoryId],
    });
  };

  const totalIncludeFilters = form.includeTagIds.length + form.includeCategoryIds.length;
  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <div className="widget-tag-filter-editor">
      <TagPicker
        id="widget-tag-filter-include-tags"
        label="Include tags"
        selectedTagIds={form.includeTagIds}
        onChange={(includeTagIds) => emit({ ...form, includeTagIds })}
      />

      {categories.length > 0 && (
        <div className="widget-tag-filter-editor__categories">
          <span className="widget-tag-filter-editor__label">Include any tag in category</span>
          <div className="widget-tag-filter-editor__category-list" role="group">
            {categories.map((category) => (
              <ForgeButton
                key={category.entityId}
                type="button"
                variant={
                  form.includeCategoryIds.includes(category.entityId) ? 'raised' : 'outlined'
                }
                onClick={() => toggleCategory(category.entityId)}
                data-testid={`widget-tag-filter-category-${category.entityId}`}
              >
                {category.name}
              </ForgeButton>
            ))}
          </div>
        </div>
      )}

      {totalIncludeFilters > 1 && (
        <div
          className="widget-tag-filter-editor__combiner"
          role="group"
          aria-label="Combine include filters"
        >
          <span className="widget-tag-filter-editor__label">Match</span>
          {(['AND', 'OR'] as TagFilterCombiner[]).map((combiner) => (
            <ForgeButton
              key={combiner}
              type="button"
              variant={form.combiner === combiner ? 'raised' : 'outlined'}
              onClick={() => emit({ ...form, combiner })}
              data-testid={`widget-tag-filter-combiner-${combiner}`}
            >
              {combiner === 'AND' ? 'All' : 'Any'}
            </ForgeButton>
          ))}
        </div>
      )}

      <TagPicker
        id="widget-tag-filter-exclude-tags"
        label="Exclude tags"
        selectedTagIds={form.excludeTagIds}
        onChange={(excludeTagIds) => emit({ ...form, excludeTagIds })}
      />
    </div>
  );
}
